/**
 * Stats sub-tab bar (Drops, Texts, List, Gateways, etc.)
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import {
  Bot,
  Gauge,
  Database,
  FileText,
  Gift,
  Layout,
  List,
  Phone,
  Route,
  Rocket,
  Router,
  Send,
  Workflow,
} from 'lucide-react';

import { Button } from '@/components/ui';

import type { StatsSubTab } from '../constants';
import { statsSubTabsFor } from '../tracker';
import type { StatsTrafficProvider } from '../types';

interface StatsSubTabBarProps {
  value: StatsSubTab;
  onChange: (tab: StatsSubTab) => void;
  /** When true, only Phone tab is shown (data-analyst). */
  phoneOnly?: boolean;
  /** Tabs follow the tracker's capabilities (Flow / Scheduler need the Flow Optimizer). */
  trafficProvider?: StatsTrafficProvider;
  /** The Scheduler tab is gated on flows.view (same as the /scheduler page). */
  canViewScheduler?: boolean;
  /** The Automation tab (the Drop Generator) is gated on drop_generator.view. */
  canViewAutomation?: boolean;
}

const ALL_TABS: Array<{ id: StatsSubTab; label: string; icon: FC<{ className?: string }> }> = [
  { id: 'drops', label: 'Drops', icon: Send },
  { id: 'texts', label: 'Texts', icon: FileText },
  { id: 'lists', label: 'List', icon: List },
  { id: 'gateways', label: 'Gateways', icon: Router },
  { id: 'sources', label: 'Sources', icon: Database },
  { id: 'phone', label: 'Phone', icon: Phone },
  { id: 'deployed', label: 'Deployed', icon: Rocket },
  { id: 'paths', label: 'Paths', icon: Route },
  { id: 'landers', label: 'Landers', icon: Layout },
  { id: 'offers', label: 'Offers', icon: Gift },
  { id: 'flow', label: 'Flow', icon: Workflow },
  { id: 'scheduler', label: 'Optimiser', icon: Gauge },
  { id: 'automation', label: 'Automation', icon: Bot },
];

export const StatsSubTabBar: FC<StatsSubTabBarProps> = ({
  value,
  onChange,
  phoneOnly = false,
  trafficProvider = 'VOLUUM',
  canViewScheduler = false,
  canViewAutomation = false,
}) => {
  const visibleTabs = useMemo(() => {
    if (phoneOnly) return ALL_TABS.filter((t) => t.id === 'phone');
    const gate = (t: { id: StatsSubTab }) =>
      (t.id !== 'scheduler' || canViewScheduler) &&
      (t.id !== 'automation' || canViewAutomation);
    const allow = statsSubTabsFor(trafficProvider);
    return ALL_TABS.filter((t) => allow.has(t.id) && gate(t));
  }, [phoneOnly, trafficProvider, canViewScheduler, canViewAutomation]);

  if (visibleTabs.length === 0) {
    return null;
  }

  return (
    <div
      role="tablist"
      aria-label="Campaign stats views"
      className="min-w-0 w-full overflow-x-auto overscroll-x-contain rounded-lg border border-border/60 bg-muted/30 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
    >
      <div className="flex h-9 w-max min-w-full items-center gap-1 p-1">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <Button
            key={id}
            role="tab"
            aria-selected={value === id}
            variant={value === id ? 'default' : 'ghost'}
            size="sm"
            className="h-7 shrink-0 gap-1.5 px-2 sm:gap-2 sm:px-3"
            onClick={() => onChange(id)}
            aria-label={label}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap">{label}</span>
          </Button>
        ))}
      </div>
    </div>
  );
};
